import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { Drawer } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { IntakeRenderer } from '@helios/ui/intake';
import type { IntakeQuestionnaire, IntakeAnswers } from '@helios/ui/intake';

// ─── Types ───

interface UpdateMedicationDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandTokens: Record<string, string>;
  initialAnswers: IntakeAnswers;
  questionnaire: IntakeQuestionnaire;
  validationSchema: string | null;
  onSave: (answers: IntakeAnswers) => void;
}

// ─── Main Component ───

export function UpdateMedicationDrawer({
  open,
  onOpenChange,
  brandTokens,
  initialAnswers,
  questionnaire,
  validationSchema,
  onSave,
}: UpdateMedicationDrawerProps) {
  const [localAnswers, setLocalAnswers] = useState<IntakeAnswers>(initialAnswers);
  const didHydrateRef = useRef(false);

  // Hydrate once on open; don't clobber while user is editing
  useEffect(() => {
    if (open && !didHydrateRef.current) {
      setLocalAnswers(initialAnswers);
      didHydrateRef.current = true;
    }
    if (!open) didHydrateRef.current = false;
  }, [open, initialAnswers]);

  const handleSave = () => {
    onSave(localAnswers);
    onOpenChange(false);
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="Update Medication"
      portalStyle={brandTokens as CSSProperties}
      footer={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Button variant="outline" size="lg" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="gradient" size="lg" onClick={handleSave}>
            Save Changes
          </Button>
        </div>
      }
    >
      <div
        style={{
          padding: 'var(--sp-6, 24px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-5, 20px)',
        }}
      >
        <IntakeRenderer
          questionnaire={questionnaire}
          value={localAnswers}
          onChange={setLocalAnswers}
          errors={[]}
          showFieldErrors={false}
          validationSchema={validationSchema}
        />
      </div>
    </Drawer>
  );
}
